import { FC, useCallback } from 'react';
import { useUnmount } from 'usehooks-ts';

import { LogicalFields } from './components/LogicalFields';
import { SidebarWrap } from './SidebarWrap';
import { deselectNodesEdges } from '../../features/graph/graphSlice';
import {
  selectCommentsById,
  selectConditionalTaskPropertiesForm,
  selectConditionalTaskPropertiesStrict,
} from '../../features/workflow/selectors';
import {
  setComment,
  updateTaskProperties,
} from '../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  type ConditionalTaskProperties,
  PropertiesType,
  type TaskPropertiesForm,
} from '../../types';
import { createConditionFormPatch } from '../../utils/taskProperties';
import { ConditionPropertiesForm } from '../../zodSchemas/conditionProperties';

export const useUpdateConditionProperties = (id: string) => {
  const dispatch = useAppDispatch();
  const prevProperties = useAppSelector(selectConditionalTaskPropertiesStrict)[
    id
  ] as ConditionalTaskProperties;

  return useCallback(
    (formUpdate: Partial<ConditionPropertiesForm>) => {
      const propertiesPatch = createConditionFormPatch(
        formUpdate,
        prevProperties
      );
      dispatch(
        updateTaskProperties({
          ...propertiesPatch,
          id,
          propertiesType: PropertiesType.Conditional,
        })
      );
    },
    [id, prevProperties, dispatch]
  );
};

interface LogicalSidebarProps {
  selectedNodeId: string;
}

export const LogicalSidebar: FC<LogicalSidebarProps> = ({ selectedNodeId }) => {
  const dispatch = useAppDispatch();
  const commitChanges = useUpdateConditionProperties(selectedNodeId);
  const comment = useAppSelector(selectCommentsById)[selectedNodeId];
  const taskPropertiesForm = useAppSelector(
    selectConditionalTaskPropertiesForm
  )[selectedNodeId];

  const handleSubmitForm = useCallback(
    (override?: Partial<TaskPropertiesForm>) => {
      const allChanges = { ...taskPropertiesForm, ...override };

      if (Object.keys(allChanges).length > 0) {
        commitChanges(allChanges);
      }
    },
    [commitChanges, taskPropertiesForm]
  );

  const updateConditionProperties =
    useUpdateConditionProperties(selectedNodeId);

  const handleCloseSidebar = useCallback(() => {
    dispatch(deselectNodesEdges());
  }, [dispatch]);

  const handleSetComment = useCallback(
    (comment: string) => dispatch(setComment({ id: selectedNodeId, comment })),
    [selectedNodeId, dispatch]
  );

  useUnmount(() => handleSubmitForm());

  if (!taskPropertiesForm) return null;

  return (
    <SidebarWrap
      key={selectedNodeId}
      comment={comment}
      sideBarTitle="Condition"
      handleCloseSidebar={handleCloseSidebar}
      handleSetComment={handleSetComment}
    >
      <LogicalFields
        id={selectedNodeId}
        values={taskPropertiesForm}
        updateTaskProperties={updateConditionProperties}
      />
    </SidebarWrap>
  );
};
